// frontend/packages/business-console/src/components/media/UploadQueuePanel.jsx
//
// Progress for a multi-file upload batch: one row per file with its status,
// plus "Copy cld: tokens" for everything that finished, so a whole batch can
// be pasted into content in one go.

import React from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { toCldToken } from '../../utils/mediaToken';

export default function UploadQueuePanel({ queue, busy, onRetry, onClear, onCopy }) {
  if (!queue?.length) return null;

  const done = queue.filter((it) => it.status === 'done');
  const failed = queue.filter((it) => it.status === 'error');
  const tokens = done.map((it) => toCldToken(it.publicId)).filter(Boolean);

  const summary = busy
    ? `Uploading… ${done.length} of ${queue.length} done`
    : `${done.length} uploaded${failed.length ? `, ${failed.length} failed` : ''}`;

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 1 }}
      >
        <Typography sx={{ fontWeight: 900, fontSize: 14 }}>{summary}</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            disabled={!tokens.length}
            onClick={() => onCopy?.(tokens)}
            sx={{ fontWeight: 800 }}
          >
            Copy {tokens.length || ''} cld: tokens
          </Button>
          <Button
            size="small"
            disabled={busy}
            onClick={onClear}
            sx={{ fontWeight: 800 }}
          >
            Clear
          </Button>
        </Stack>
      </Stack>

      {!busy && done.length > 0 ? (
        <Alert severity="info" sx={{ mb: 1 }}>
          New uploads need alt text. Open each one with Details to add alt text, a
          title, a folder, and tags.
        </Alert>
      ) : null}

      <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
        <Stack spacing={0.75}>
          {queue.map((it) => (
            <Stack key={it.id} direction="row" alignItems="center" spacing={1}>
              <Box sx={{ width: 20, display: 'grid', placeItems: 'center' }}>
                {it.status === 'done' ? (
                  <CheckCircleIcon color="success" fontSize="small" />
                ) : it.status === 'error' ? (
                  <ErrorOutlineIcon color="error" fontSize="small" />
                ) : null}
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 700 }}>
                  {it.name}
                </Typography>
                {it.status === 'error' ? (
                  <Typography noWrap sx={{ fontSize: 11.5, color: 'error.main' }}>
                    {it.error}
                  </Typography>
                ) : (
                  <LinearProgress
                    variant={it.status === 'queued' ? 'indeterminate' : 'determinate'}
                    value={Math.round((it.progress || 0) * 100)}
                    sx={{ mt: 0.5, opacity: it.status === 'queued' ? 0.35 : 1 }}
                  />
                )}
              </Box>
              {it.status === 'error' ? (
                <Tooltip title="Retry">
                  <IconButton size="small" aria-label={`Retry ${it.name}`} onClick={() => onRetry?.(it.id)}>
                    <ReplayIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Stack>
          ))}
        </Stack>
      </Box>
    </Paper>
  );
}
